import React from 'react'

import { FormBuilder, Spinner } from '@acorns/web-components'

import { APP_STORE_URL, GOOGLE_PLAY_URL } from 'app-constants'
import AppStore2Button from 'assets/icons/app-store-2.svg'
import AppStoreButton from 'assets/icons/app-store.svg'
import GooglePlay2Button from 'assets/icons/google-play-2.svg'
import GooglePlayButton from 'assets/icons/google-play.svg'
import LaterLogoImage from 'assets/icons/later-logo.png'
import SuccessMedal from 'assets/images/medal.png'
import SuccessImage from 'assets/images/new-success.png'
import Button from 'components/Button'
import { getRemoteStrings } from 'components/HOCs/withRemoteStrings/withRemoteStrings'
import Layout from 'components/Layout'
import Nav from 'components/Nav'
import { withClickTrack } from 'components/Trackers/ClickTracker'
import { isAndroid, isApple, isMobile } from 'components/Trackers/platform'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { TestIDs } from 'test/utils/testIds'
import { isDefaultFlow } from 'utils/FlowProvider/utils'

import { enhance } from './enhance'
import {
  AppStoreButtons,
  ButtonContainer,
  ChangeEmailButton,
  EmailAddress,
  GreenCopy,
  Icon,
  Image,
  ImageContainer,
  Message,
  PartnerHeading,
  PartnerLink,
  PartnerSubheading,
  StyledSeparator,
  SuccessIcon,
  TextContainer,
  Title,
} from './styled-components'
import { InnerProps } from './types'

const buttons = [
  {
    image: AppStoreButton,
    href: APP_STORE_URL,
    trackerId: TRACKER_IDS.SUCCESS.APP_STORE_BUTTON,
    testId: TestIDs.SUCCESS.APP_STORE,
    altText: 'Apple Store',
  },
  {
    image: GooglePlayButton,
    href: GOOGLE_PLAY_URL,
    trackerId: TRACKER_IDS.SUCCESS.GOOGLE_PLAY_BUTTON,
    testId: TestIDs.SUCCESS.GOOGLE_PLAY,
    altText: 'Google Play',
  },
]

const laterButtons = [
  {
    image: AppStore2Button,
    href: APP_STORE_URL,
    trackerId: TRACKER_IDS.SUCCESS.APP_STORE_BUTTON,
    testId: TestIDs.SUCCESS.APP_STORE,
    altText: 'Apple Store',
  },
  {
    image: GooglePlay2Button,
    href: GOOGLE_PLAY_URL,
    trackerId: TRACKER_IDS.SUCCESS.GOOGLE_PLAY_BUTTON,
    testId: TestIDs.SUCCESS.GOOGLE_PLAY,
    altText: 'Google Play',
  },
]

const laterSuccessCopy = (hasInvested, investedAmount, investedFrequency) => {
  if (!hasInvested) {
    return 'You’ve taken the first step to start investing for your future.'
  } else if (investedFrequency) {
    const frequency = investedFrequency === 'daily' ? 'day' : investedFrequency.replace('ly', '')
    return (
      <>
        Congratulations! Your bank account is all linked up and you're investing{' '}
        <GreenCopy>
          ${investedAmount} each {frequency}
        </GreenCopy>{' '}
        into your Later account.
      </>
    )
  } else {
    return (
      <>
        Congratulations! Your bank account is all linked up and you've invested ${investedAmount}{' '}
        into your Later account.
      </>
    )
  }
}

const TrackAppStoreButton = (button, index) => {
  const TrackedAppStoreButton = withClickTrack<any, any>(button.trackerId)(AppStoreButtons)

  return (
    <TrackedAppStoreButton key={index} href={button.href} data-testid={button.testId}>
      <img src={button.image} alt={button.altText} width="100%" />
    </TrackedAppStoreButton>
  )
}

const PartnerRedirect = ({ partnerName, handlePartnerRedirect }) => (
  <>
    <PartnerHeading>{`You are now being redirected back to ${partnerName}`}</PartnerHeading>
    <PartnerSubheading>
      {'If this page does not already redirect,'}{' '}
      <PartnerLink
        onClick={handlePartnerRedirect}
        data-testid={TestIDs.SUCCESS.PARTNER_REDIRECT_LINK}
      >
        tap here
      </PartnerLink>
    </PartnerSubheading>
  </>
)

const ShowChangeEmail = ({
  rows,
  errors,
  values,
  handleChange,
  setEmailFieldValue,
  changeEmail,
  loading,
  submitting,
  handleCancelChangeEmail,
  submitForm,
}) => (
  <>
    <TextContainer className="fully-responsive-padding">
      <Title>You're all done!</Title>
      <Message>We will send you a confirmation email at:</Message>
      <FormBuilder
        rows={rows}
        errors={errors}
        values={values}
        handleChange={handleChange}
        setFieldValue={setEmailFieldValue}
      />
    </TextContainer>
    <StyledSeparator withMargin={!changeEmail || loading} />
    <ButtonContainer>
      <ChangeEmailButton
        inkOpacity={0.02}
        disabled={submitting}
        secondary
        onPress={handleCancelChangeEmail}
      >
        Cancel
      </ChangeEmailButton>
      <ChangeEmailButton onPress={submitForm} disabled={submitting}>
        {submitting ? <Spinner height={20} width={20} /> : 'Update Email'}
      </ChangeEmailButton>
    </ButtonContainer>
  </>
)

type AllDoneProps = {
  loading: boolean
  flow: string
  handleOnLink: () => void
}

const AllDone = ({ loading, flow, handleOnLink }: AllDoneProps) => (
  <>
    <TextContainer className="fully-responsive-padding">
      <SuccessIcon src={SuccessImage} />
      <Title data-testid={TestIDs.SUCCESS.SUCCESS_MESSAGE}>Nice Work!</Title>
      <Message>
        Download the Acorns app or log in to explore your Invest account and easily set up the rest
        of your accounts included in your subscription.
      </Message>
    </TextContainer>
    <StyledSeparator withMargin={loading} />
    <ButtonContainer>
      {showAppStoreButtons(flow) ? (
        <Button onPress={handleOnLink} data-testid={TestIDs.SUCCESS.OPEN_ACORNS_CTA}>
          Open Acorns
        </Button>
      ) : (
        (isAndroid() ? [buttons[1]] : isApple() ? [buttons[0]] : []).map((button, index) =>
          TrackAppStoreButton(button, index),
        )
      )}
    </ButtonContainer>
  </>
)

const LaterSuccess = ({
  updatedEmail,
  email,
  changeEmail,
  loading,
  hasInvested,
  investedAmount,
  investedFrequency,
  handleOnLink,
  flow,
}) => (
  <>
    <TextContainer>
      <Icon src={SuccessMedal} />
      <Title data-testid={TestIDs.SUCCESS.LATER_TITLE}>
        {hasInvested ? 'You’re now an investor' : 'Congratulations!'}
      </Title>
      <Message data-testid={TestIDs.SUCCESS.LATER_TEXT}>
        {laterSuccessCopy(hasInvested, investedAmount, investedFrequency)}
      </Message>
      <Message>
        We’ll send you a confirmation email at:{' '}
        <EmailAddress>{updatedEmail ? updatedEmail : email}</EmailAddress>
      </Message>
    </TextContainer>
    <StyledSeparator withMargin={!changeEmail || loading} />
    <ButtonContainer>
      {showAppStoreButtons(flow) ? (
        <Button onPress={handleOnLink} data-testid={TestIDs.SUCCESS.OPEN_ACORNS_CTA}>
          Open Acorns
        </Button>
      ) : (
        (isAndroid()
          ? [laterButtons[1]]
          : isApple()
          ? [laterButtons[0]]
          : []
        ).map((button, index) => TrackAppStoreButton(button, index, 'later'))
      )}
    </ButtonContainer>
  </>
)

const LaterOnboardingSuccess = ({
  handleOnLink,
  hasInvested,
  investedAmount,
  investedFrequency,
}) => (
  <>
    <TextContainer>
      <Title data-testid={TestIDs.SUCCESS.LATER_ONBOARDING.TITLE}>
        {getRemoteStrings('later_success_title', 'The future is bright!', 'laterOnboarding')}
      </Title>
      <Message>
        {getRemoteStrings(
          'later_success_subtitle',
          "You're ready to invest in the life you want Later",
          'laterOnboarding',
        )}
      </Message>
      <Message>{laterSuccessCopy(hasInvested, investedAmount, investedFrequency)}</Message>
      <ButtonContainer>
        <Button onPress={handleOnLink} data-testid={TestIDs.SUCCESS.LATER_ONBOARDING.SUBMIT_CTA}>
          {getRemoteStrings('later_success_cta', 'Explore My Account', 'laterOnboarding')}
        </Button>
      </ButtonContainer>
    </TextContainer>
    <ImageContainer>
      <Image src={LaterLogoImage} data-testid={TestIDs.SUCCESS.LATER_ONBOARDING.IMAGE} />
    </ImageContainer>
  </>
)

const showAppStoreButtons = flow => {
  return flow === 'later' || (flow === 'tiered' && !isMobile())
}

const linkText = flow => {
  if (flow === 'later' && isMobile()) {
    return 'Log in'
  } else if (flow !== 'later') {
    return 'Log in'
  } else {
    return null
  }
}

const onLinkHandler = (flow, handleOnLink) => {
  if ((flow === 'later' && isMobile()) || flow !== 'later') {
    return handleOnLink
  } else {
    return null
  }
}

export const Success: React.FC<InnerProps> = ({
  changeEmail,
  email,
  errors,
  handleCancelChangeEmail,
  handleChange,
  handleOnLink,
  handlePartnerRedirect,
  loading,
  partnerName,
  redirectUrl,
  rows,
  setEmailFieldValue,
  submitForm,
  submitting,
  updatedEmail,
  values,
  flow,
  hasInvested,
  investedAmount,
  investedFrequency,
}) => (
  <>
    <Nav
      linkText={linkText(flow)}
      onLink={onLinkHandler(flow, handleOnLink)}
      skipClickTracker={flow === 'later' ? null : TRACKER_IDS.SUCCESS.LOGIN_BUTTON}
    />
    <Layout heroImage={null} showAppStoreButtons={showAppStoreButtons(flow)}>
      <>
        {loading && <Spinner />}

        {!loading && redirectUrl && <PartnerRedirect {...{ partnerName, handlePartnerRedirect }} />}

        {!loading && !redirectUrl && isDefaultFlow() && changeEmail && (
          <ShowChangeEmail
            {...{
              rows,
              errors,
              values,
              handleChange,
              setEmailFieldValue,
              changeEmail,
              loading,
              submitting,
              handleCancelChangeEmail,
              submitForm,
            }}
          />
        )}

        {!loading && !redirectUrl && isDefaultFlow() && !changeEmail && (
          <AllDone
            {...{
              loading,
              flow,
              handleOnLink,
            }}
          />
        )}

        {!loading && !redirectUrl && flow === 'later' && (
          <LaterSuccess
            {...{
              updatedEmail,
              email,
              changeEmail,
              loading,
              hasInvested,
              investedAmount,
              investedFrequency,
              handleOnLink,
              flow,
            }}
          />
        )}

        {!loading && !redirectUrl && flow === 'laterOnboarding' && (
          <LaterOnboardingSuccess
            {...{
              loading,
              handleOnLink,
              hasInvested,
              investedAmount,
              investedFrequency,
              flow,
            }}
          />
        )}
      </>
    </Layout>
  </>
)

export default enhance(Success)
